import React, { useState, useEffect } from "react";
import { Bookmark, X } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface TutorialActionsProps {
  id: string;
  saved?: boolean;
  isUninterested?: boolean;
  onSave?: (e: React.MouseEvent) => void;
  onNotInterested?: (e: React.MouseEvent) => void;
  classNames?: {
    wrapper?: string;
    button?: string;
    buttonText?: string;
  };
  isOnlyForSave?: boolean;
  muted?: boolean;
}

export const TutorialActions = ({
  id,
  saved = false,
  onSave,
  onNotInterested,
  classNames,
  isUninterested = false,
  isOnlyForSave = false,
  muted = false,
}: TutorialActionsProps) => {
  const { t } = useTranslation();

  // Detect touch device
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice(window.matchMedia("(hover: none)").matches);
  }, []);

  // Handle touch interactions
  const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    // Add touch-device class for CSS targeting
    e.currentTarget.classList.add("touch-device");
  };

  return (
    <div
      className={cn(
        `flex items-center gap-2 shrink-0 justify-end ${classNames?.wrapper}`
      )}
    >
      {!isOnlyForSave && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            `h-9 text-sm font-medium transition-colors duration-150 flex items-center gap-2 ${classNames?.button}`,
            isUninterested
              ? "text-destructive bg-destructive/5 active:bg-destructive/10"
              : isTouchDevice
              ? "text-foreground active:text-destructive active:bg-destructive/10"
              : "text-foreground hover:text-destructive active:bg-destructive/10",
            {
              "cursor-not-allowed": muted,
            }
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!muted) {
              onNotInterested && onNotInterested(e);
            }
          }}
          onTouchStart={(e) => handleTouchStart(e)}
        >
          <X className="w-[26px] h-[26px]" />
          <span
            className={`ml-2 hidden xl:inline-block ${classNames?.buttonText}`}
          >
            {t("tutorial.actions.notInterested")}
          </span>
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn(
          `h-9 transition-colors duration-150 flex items-center gap-2 ${classNames?.button}`,
          saved
            ? "text-primary bg-primary/5 active:bg-primary/10"
            : isTouchDevice
            ? "text-foreground active:text-primary active:bg-primary/10"
            : "text-foreground hover:text-primary active:bg-primary/10",
          {
            "cursor-not-allowed": muted,
          }
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!muted) {
            onSave && onSave(e);
          }
        }}
        onTouchStart={(e) => handleTouchStart(e)}
      >
        <Bookmark
          className="w-[26px] h-[26px]"
          fill={saved ? "currentColor" : "none"}
        />
        <span
          className={`ml-2 hidden xl:inline-block ${classNames?.buttonText}`}
        >
          {saved ? t("tutorial.actions.saved") : t("tutorial.actions.save")}
        </span>
      </Button>
    </div>
  );
};
